/**
 * Storage Factory
 *
 * Factory for creating storage adapters.
 *
 * SQL storage (via the BFF + Postgres) is the default and only data backend
 * (#599 retired the legacy localStorage-backed path). localStorage is still used
 * as a transient fallback before auth is ready, and when a caller explicitly
 * passes `forceType: 'local'`.
 *
 * @example
 * ```ts
 * const adapter = createStorageAdapter<SurveyData>('survey', authService);
 * // Returns LocalStorageAdapter or SqlStorageAdapter based on feature flag
 * ```
 */

import type { IStorageAdapter } from './IStorageAdapter'
import { LocalStorageAdapter } from './LocalStorageAdapter'
import { SqlStorageAdapter } from './SqlStorageAdapter'

/**
 * Auth service interface (minimal required interface)
 */
export interface AuthService {
  getAccessToken(): Promise<string>
  isAuthenticated: boolean
}

/**
 * Storage adapter factory options
 */
export interface StorageFactoryOptions {
  /** Force a specific adapter type (overrides feature flag) */
  forceType?: 'local' | 'sql'
  /** Base URL for SQL storage (BFF server) */
  baseUrl?: string
  /** Custom retry configuration for SQL storage */
  retryConfig?: {
    maxRetries?: number
    initialDelay?: number
    maxDelay?: number
    backoffMultiplier?: number
  }
}

/**
 * Create a storage adapter based on configuration
 *
 * @param storageType Type of storage (e.g., 'survey', 'site', 'assumptions')
 * @param authService Auth service for SQL storage authentication
 * @param options Factory options
 * @returns Storage adapter instance
 */
export function createStorageAdapter<T>(
  storageType: string,
  authService?: AuthService,
  options: StorageFactoryOptions = {}
): IStorageAdapter<T> {
  // SQL is the default backend; localStorage only when explicitly forced.
  const useSqlStorage = options.forceType !== 'local'

  if (useSqlStorage) {
    if (!authService) {
      // Fallback to localStorage if SQL storage is requested but authService not available
      // This happens during store initialization before auth is ready
      // The storage will be re-initialized with auth once auth is ready (via useStorageInitialization)
      return new LocalStorageAdapter<T>(storageType)
    }

    return new SqlStorageAdapter<T>(storageType, authService, options.baseUrl, options.retryConfig)
  }

  // Default to localStorage
  return new LocalStorageAdapter<T>(storageType)
}

/**
 * Whether SQL storage is the backend. Always true since #599 retired the
 * localStorage-backed data path (localStorage now only serves as a transient
 * pre-auth / explicitly-forced fallback).
 */
export function isSqlStorageEnabled(): boolean {
  return true
}

/**
 * Get the storage adapter type. Always 'sql' since #599 (see isSqlStorageEnabled).
 */
export function getStorageAdapterType(): 'local' | 'sql' {
  return 'sql'
}
